import React, { useEffect, useMemo, useRef, useState } from 'react'
import Icon from '../../../ui/Icon/Icon'
import DropdownBox from '../../../ui/DropdownBox/DropdownBox'
import Button from '../../../ui/Button/Button'
import uniqid from 'uniqid'
import './RelatedProductSearch.sass'
import {
  countFilteredRelatedProducts,
  MAX_RELATED_PRODUCT_RESULTS,
  searchRelatedProducts,
} from '../../../api/marketing/searchRelatedProducts'

function RelatedProductSearch({
  value = null,
  onChange,
  searchIndex = [],
  filters,
  isLoading,
  loadError,
  onLoadRequest,
  canBrowseOnFocus = false,
  label = 'Повʼязаний товар',
  buttonPicker = false,
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const [activeOptionIndex, setActiveOptionIndex] = useState(-1)
  const fieldRef = useRef(null)
  const dropdownRef = useRef(null)
  const inputRef = useRef(null)
  const buttonRef = useRef(null)
  const listId = useRef(uniqid('related-products-')).current
  const optionRefs = useRef([])

  const trimmedQuery = searchQuery.trim()
  const showDropdown = isFocused && (trimmedQuery.length > 0 || canBrowseOnFocus || buttonPicker)

  const searchOptions = useMemo(() => ({ allowBrowseWithoutQuery: canBrowseOnFocus || buttonPicker }), [
    canBrowseOnFocus,
    buttonPicker,
  ])

  const searchResults = useMemo(() => searchRelatedProducts(searchIndex, filters, trimmedQuery, searchOptions), [
    searchIndex,
    filters,
    trimmedQuery,
    searchOptions,
  ])

  const totalResultCount = useMemo(
    () => countFilteredRelatedProducts(searchIndex, filters, trimmedQuery, searchOptions),
    [searchIndex, filters, trimmedQuery, searchOptions]
  )

  const isResultCapReached = totalResultCount > MAX_RELATED_PRODUCT_RESULTS
  const hasFloatingLabel = Boolean(value) || trimmedQuery.length > 0
  const inputValue = isFocused ? searchQuery : value?.label || ''

  useEffect(() => {
    if (buttonPicker && isFocused) inputRef.current?.focus()
  }, [buttonPicker, isFocused])

  useEffect(() => {
    setActiveOptionIndex(searchResults.length > 0 ? 0 : -1)
    optionRefs.current = []
  }, [searchResults])

  useEffect(() => {
    const activeOption = optionRefs.current[activeOptionIndex]

    if (activeOption) {
      activeOption.scrollIntoView({ block: 'nearest' })
    }
  }, [activeOptionIndex])

  const handleSelectProduct = (product) => {
    onChange(product)
    setSearchQuery('')
    setActiveOptionIndex(-1)
    setIsFocused(false)
    if (buttonPicker) buttonRef.current?.focus()
    else inputRef.current?.blur()
  }

  const handleOptionMouseDown = (event) => {
    event.preventDefault()
  }

  const handleFieldBlur = (event) => {
    if (!fieldRef.current?.contains(event.relatedTarget)) {
      setIsFocused(false)
      setSearchQuery('')
      setActiveOptionIndex(-1)
    }
  }

  const handleInputKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      setIsFocused(false)
      setSearchQuery('')
      if (buttonPicker) buttonRef.current?.focus()
      return
    }
    if (!showDropdown || isLoading || searchResults.length === 0) {
      return
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveOptionIndex((index) => (index < searchResults.length - 1 ? index + 1 : 0))
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveOptionIndex((index) => (index > 0 ? index - 1 : searchResults.length - 1))
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      const product = searchResults[activeOptionIndex]

      if (product) {
        handleSelectProduct(product)
      }
    }
  }

  const handleClear = (event) => {
    event.preventDefault()
    event.stopPropagation()
    onChange(null)
    setSearchQuery('')
    setActiveOptionIndex(-1)
    setIsFocused(false)
    inputRef.current?.blur()
  }

  const handleFocus = () => {
    onLoadRequest?.()
    setSearchQuery(value?.label || '')
    setIsFocused(true)
  }

  const handleInputChange = (event) => {
    const nextQuery = event.target.value
    setSearchQuery(nextQuery)

    if (value && !nextQuery.trim()) {
      onChange(null)
    }
  }

  const renderDropdownContent = () => {
    if (loadError) {
      return (
        <li role="presentation" className="UtmBuilder-RelatedProductHint UtmBuilder-RelatedProductHint_error">
          {loadError}
        </li>
      )
    }
    if (isLoading) {
      return (
        <li role="presentation" className="EmptyMessage">
          Завантаження…
        </li>
      )
    }

    if (searchResults.length === 0) {
      return (
        <li role="presentation" className="EmptyMessage">
          Товарів не знайдено
        </li>
      )
    }

    return searchResults.map((product, index) => (
      <li
        key={product.key}
        ref={(element) => {
          optionRefs.current[index] = element
        }}
        id={`${listId}-${product.key}`}
        role="option"
        aria-selected={index === activeOptionIndex}
        className={['DropdownBox-Element', index === activeOptionIndex ? 'DropdownBox-Element_theme_active' : '']
          .filter(Boolean)
          .join(' ')}
        onMouseDown={handleOptionMouseDown}
        onMouseEnter={() => setActiveOptionIndex(index)}
        onClick={() => handleSelectProduct(product)}
      >
        <div className="UtmBuilder-RelatedProductOption">
          <div className="UtmBuilder-RelatedProductOptionTitle">{product.name}</div>
          <div className="UtmBuilder-RelatedProductOptionMeta">
            <span className="UtmBuilder-RelatedProductOptionMetaLeft">{product.metaLabel}</span>
            <span className="UtmBuilder-RelatedProductOptionMetaRight">{product.sourceLabel}</span>
          </div>
        </div>
      </li>
    ))
  }

  return (
    <div
      className={`UtmBuilder-RelatedProductField${buttonPicker ? ' UtmBuilder-RelatedProductField_button' : ''}`}
      ref={fieldRef}
      onBlur={handleFieldBlur}
    >
      {buttonPicker ? (
        <Button
          ref={buttonRef}
          title={label}
          theme="bounded"
          border="gray"
          color="gray"
          icon="plus"
          aria-expanded={showDropdown}
          aria-haspopup="listbox"
          onClick={() => {
            if (!isFocused) onLoadRequest?.()
            setSearchQuery('')
            setIsFocused(!isFocused)
          }}
        />
      ) : (
        <>
          <div
            className={[
              'Field',
              'UtmBuilder-RelatedProductControl',
              hasFloatingLabel ? 'Field_with_value' : '',
              value ? 'UtmBuilder-RelatedProductControl_has_clear' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => inputRef.current?.focus()}
          >
            <input
              ref={inputRef}
              type="text"
              className="UtmBuilder-RelatedProductInput"
              value={inputValue}
              readOnly={Boolean(value) && !isFocused}
              onChange={handleInputChange}
              onFocus={handleFocus}
              onKeyDown={handleInputKeyDown}
              role="combobox"
              aria-expanded={showDropdown}
              aria-autocomplete="list"
              aria-label={label}
              aria-controls={showDropdown ? listId : undefined}
              aria-activedescendant={
                showDropdown && activeOptionIndex >= 0
                  ? `${listId}-${searchResults[activeOptionIndex]?.key}`
                  : undefined
              }
            />

            {value && !isLoading ? (
              <button
                type="button"
                className="UtmBuilder-RelatedProductClear"
                title="Очистити товар"
                onMouseDown={handleOptionMouseDown}
                onClick={handleClear}
              >
                <Icon name="times" weight="regular" size={12} />
              </button>
            ) : null}

            {isLoading && <span className="UtmBuilder-RelatedProductLoading" aria-hidden="true" />}
            <span className="Field-Bar" />
            <label>{label}</label>
          </div>

          <div className="UtmBuilder-RelatedProductSelectedMetaSlot">
            {value ? (
              <p className="UtmBuilder-RelatedProductSelectedMeta">
                {value.metaLabel ? <span>{value.metaLabel}</span> : null}
                {value.sourceLabel ? (
                  <span className="UtmBuilder-RelatedProductSelectedMetaSource">{value.sourceLabel}</span>
                ) : null}
              </p>
            ) : null}
          </div>

          {isResultCapReached && (isFocused || value) ? (
            <p className="UtmBuilder-RelatedProductHint">Показано перші 20. Уточніть пошуком.</p>
          ) : null}

          {loadError ? (
            <p className="UtmBuilder-RelatedProductHint UtmBuilder-RelatedProductHint_error">{loadError}</p>
          ) : null}
        </>
      )}

      {showDropdown && (
        <DropdownBox ref={dropdownRef} styles={{ top: buttonPicker ? '100%' : '43px' }}>
          {buttonPicker ? (
            <div className="UtmBuilder-RelatedProductSearchBox">
              <input
                ref={inputRef}
                type="search"
                className="UtmBuilder-RelatedProductSearchInput"
                placeholder="Пошук товарів"
                aria-label="Пошук товарів"
                value={searchQuery}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                role="combobox"
                aria-expanded={showDropdown}
                aria-autocomplete="list"
                aria-controls={listId}
                aria-activedescendant={
                  activeOptionIndex >= 0 ? `${listId}-${searchResults[activeOptionIndex]?.key}` : undefined
                }
              />
            </div>
          ) : null}
          <ul id={listId} role="listbox" aria-label="Товари" className="UtmBuilder-RelatedProductResults">
            {renderDropdownContent()}
          </ul>
        </DropdownBox>
      )}
    </div>
  )
}

export default RelatedProductSearch
